import styled, { css, keyframes } from 'styled-components'
import { accents, gray, semantic } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'
import { spacing } from 'sutra/tokens/spacing'

export type PhaseState = 'active' | 'complete' | 'error' | 'pending'

const rowIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`

// Stagger each phase's entry so parallel phases arrive with the cadence of
// one thought after another — not as a wall of simultaneous rows.
export const Row = styled.div<{ $index: number }>`
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: ${spacing[3]}px;
  padding: ${spacing[2]}px 0;
  animation: ${rowIn} ${motion.duration.slow}ms ${motion.ease} both;
  animation-delay: ${({ $index }) => Math.min($index * 90, 360)}ms;
`

export const RailColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 6px;
`

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(0.7); opacity: 0.6; }
`

// Navy dot for active (pulsing) and complete (static). Green was Sutra's
// emerald token but reads as unfamiliar in Yogi's product surface — the
// dot only carries "active vs done vs failed," not a success-checkmark
// convention. Crimson stays for error; gray for pending.
const dotStyles: Record<PhaseState, ReturnType<typeof css>> = {
  active: css`
    background: ${gray[500]};
    animation: ${pulse} 1.4s ease-in-out infinite;
  `,
  complete: css`
    background: ${gray[500]};
  `,
  error: css`
    background: ${accents.crimsonBloom[500]};
  `,
  pending: css`
    background: ${gray[300]};
  `,
}

export const StatusDot = styled.span<{ $state: PhaseState }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  ${({ $state }) => dotStyles[$state]}
`

export const RailLine = styled.span<{ $active: boolean }>`
  flex: 1;
  width: 1px;
  min-height: ${spacing[3]}px;
  background: ${({ $active }) => ($active ? gray[500] : gray[200])};
  opacity: ${({ $active }) => ($active ? 0.4 : 1)};
  margin-top: ${spacing[1]}px;
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]}px;
  min-width: 0;
`

/**
 * The reasoning text — the WHY.
 *
 * Kept only slightly heavier than the call lines below it. Earlier
 * treatment (14px / weight 500 / semantic.fg) stacked THREE axes of
 * "louder" (size + weight + contrast) which made every phase's reasoning
 * feel like a heading. One axis of subtle emphasis is enough: 1px larger
 * than the calls, same weight, and a slightly darker gray. Users still
 * read it first, but it no longer dominates the actions.
 *
 * Uses <div> because reasoning renders through ReactMarkdown, which emits
 * <p> tags at the block level. Nested paragraph margins are zeroed and
 * consecutive paragraphs get a small top gap.
 */
export const Rationale = styled.div`
  font-size: 13px;
  line-height: 20px;
  color: ${gray[500]};
  font-weight: 400;

  & p {
    margin: 0;
  }
  & p + p {
    margin-top: ${spacing[1]}px;
  }
  & strong {
    font-weight: 600;
  }
  & em {
    font-style: italic;
  }
`

export const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]}px;
  font-size: 12px;
  line-height: 18px;
  color: ${gray[500]};
`

/**
 * One-line-per-call list. Stacked lines, no bullet marker — the phase's
 * rail + status dot already anchors "this is one phase"; a leading marker
 * per row would double-anchor. Reads as siblings via size/weight/color
 * (12px muted vs the 14px darker rationale above).
 */
export const CallList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]}px;
  margin-top: ${spacing[1]}px;
`

export const CallLine = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: ${gray[500]};
`

/**
 * Divergent-per-call detail rendered as a sub-line indented under the
 * verb ("Pulled quotes / + sentiment: Positive"). Sub-line beats an inline
 * separator because the eye reads hierarchy immediately (verb + its extra
 * filter) and long divergent values wrap under their own anchor instead
 * of pushing the verb around.
 */
export const DivergentDetail = styled.div`
  padding-left: ${spacing[3]}px;
  color: ${gray[500]};
  font-size: 11px;
  line-height: 16px;
  margin-top: 2px;
`

export const ChevronToggle = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 0;
  border: none;
  background: none;
  color: ${gray[500]};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  border-radius: ${radii.xs}px;
  transition:
    background-color ${motion.duration.fast}ms ${motion.ease},
    color ${motion.duration.fast}ms ${motion.ease};

  & svg {
    width: 12px;
    height: 12px;
    transition: transform ${motion.duration.fast}ms ${motion.ease};
    transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  }

  &:hover {
    background: ${gray[100]};
    color: ${semantic.fg};
  }
`

const detailsIn = keyframes`
  from { opacity: 0; transform: translateY(-2px); }
  to   { opacity: 1; transform: translateY(0); }
`

// No box, no border, no background — just indented labeled text so the
// details read as a continuation of the phase, not a widget inside it. The
// short left border echoes the phase rail one level in.
export const Details = styled.div`
  margin-top: ${spacing[1]}px;
  padding-left: ${spacing[2]}px;
  border-left: 1px solid ${gray[200]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]}px;
  animation: ${detailsIn} ${motion.duration.base}ms ${motion.ease} both;
`

// Two-column table layout. Label sits in a fixed 96px column on the left;
// value takes the rest of the row and wraps WITHIN its column when long
// (previous flex-wrap layout let long values fall to a new row under the
// label, which broke the reader's scan pattern).
export const DetailLine = styled.div`
  display: grid;
  grid-template-columns: 96px 1fr;
  column-gap: ${spacing[2]}px;
  align-items: baseline;
  font-size: 12px;
  line-height: 18px;
`

export const DetailLabel = styled.span`
  color: ${gray[500]};
  font-weight: 500;
`

export const DetailValue = styled.span`
  color: ${semantic.fg};
  font-weight: 400;
`

export type OutcomeTone = 'ok' | 'error' | 'neutral'

const outcomeStyles: Record<OutcomeTone, ReturnType<typeof css>> = {
  ok: css`
    color: ${gray[600]};
  `,
  error: css`
    color: ${accents.crimsonBloom[600]};
  `,
  neutral: css`
    color: ${gray[500]};
  `,
}

// Sits under the verb-group's divergent lines, at the same left indent —
// visually subordinate to the verb above and clearly the answer this
// verb-group produced, not a peer of the next verb-line.
export const Outcome = styled.div<{ $tone: OutcomeTone }>`
  padding-left: ${spacing[3]}px;
  font-size: 12px;
  line-height: 18px;
  margin-top: ${spacing[1]}px;
  font-variant-numeric: tabular-nums;
  ${({ $tone }) => outcomeStyles[$tone]}
`
